import { DialogController } from 'aurelia-dialog';
import { autoinject, PLATFORM } from 'aurelia-framework';
import highlight from 'highlight.js';

import { AcurityTable } from '~/components/acurity-table/acurity-table';
import { BaseDialog, BaseDialogModel } from '~/components/dialog/base-dialog';
import { DrilldownCommonModel } from '~/components/dialog/dialog-with-tabs/drilldown-model';
import { ShortcutKey } from '~/constants';
import { JQueryHelper } from '~/utils/jquery-helper';
import { Key } from '~/utils/key';
import { KeyboardShortcut } from '~/utils/keyboard-shortcut';
import { Tab } from './tab-model';

export interface DialogWithTabsModel extends BaseDialogModel {
	drilldownModel: DrilldownCommonModel;
	parentTable: AcurityTable;
	recordId: string;
	tabTemplates: Tab[];
}

/**
 * Dialog With Tabs
 *
 * This component creates a modal dialog with a summary displayed on top, as well named tabs which can be selected.
 *
 * view-model objects are provided to the component as the header and tabs.
 *
 * @param headerTemplate: ViewModel which shows the top panel information above the tabs.
 * @param tabTemplates (Tab): name:view-model pairs with the tab name and view-model
 */
@autoinject
export class DialogWithTabs extends BaseDialog<DialogWithTabsModel> {
	private dialogId: string;
	private tabSelectedFunction: (tabId: string) => void;
	private headerTemplate: object;
	private tabTemplates: Tab[];

	private resizeFunction: () => void;

	constructor(
		readonly controller: DialogController,
		readonly element: Element,
		...rest
	) {
		super(...rest);
		PLATFORM.moduleName('./dialog-with-tabs');
	}

	public activate(model: DialogWithTabsModel) {
		return super.activate(model);
	}

	public attached() {
		this.dialogId = 'dialog-with-tabs-' + this.model.recordId;
		this.setTemplates();
		this.setOnTabSelected();
		this.setResizeFunction();
		JQueryHelper.whenReady(
			$(
				'#' +
					this.dialogId +
					' #' +
					this.dialogId +
					'-tablist' +
					' a:first',
			),
		).then(() => {
			$(
				'#' +
					this.dialogId +
					' #' +
					this.dialogId +
					'-tablist' +
					' a:first',
			).tab('show'); // show the first tab

			if (this.model.drilldownModel.isArchive) {
				this.element
					.querySelectorAll(
						'.drilldown-tab-content > .header-title > h3',
					)[0]
					.classList.add('archive');
			}
		});
		KeyboardShortcut.subscribe(
			this,
			ShortcutKey.Close,
			this.element.querySelectorAll('[click\\.trigger="close()"]'),
		);
	}

	private setTemplates() {
		this.setHeaderTemplate(this.model.drilldownModel, (error, result) => {
			this.setTabTemplates(this.model.tabTemplates);
		});
	}

	public setHeaderTemplate(
		headerTemplate: object,
		notifyWhenDone?: (error?: Error, result?: any) => void,
	) {
		this.overrideAttachedMethodOnModel(headerTemplate, notifyWhenDone);

		this.headerTemplate = headerTemplate;
	}

	public setTabTemplates(tabTemplates: Tab[]) {
		this.tabTemplates = tabTemplates;
	}

	/**
	 * When the child view-models are updated asynchronously we want to be able to catch the event when they are finished,
	 * otherwise there may be issues opening the modal
	 */
	private overrideAttachedMethodOnModel(model, callback) {
		const originalAttachedMethod = model.attached;

		model.attached = () => {
			if (originalAttachedMethod) {
				originalAttachedMethod();
			}
			callback();
		};
	}

	private tabClicked(tabId: string) {
		if (this.tabSelectedFunction) {
			this.tabSelectedFunction(tabId);
		}
	}

	private setOnTabSelected() {
		this.tabSelectedFunction = (tabId) => {
			// redraw all the tables in this tab.
			// If a table had a loading spinner, and this completed while another tab was selected, the table will need to be redrawn.
			if (
				$(tabId)
					.find('acurity-table')
					.parent('.section')
					.prev('.section')
					.hasClass('a-table-loading-container')
			) {
				$(tabId + ' acurity-table table')
					.DataTable()
					.draw();
			}
			$('#' + this.dialogId + ' ' + tabId + ' pre code').each(
				(i, block) => {
					highlight.highlightBlock(block);
				},
			);
		};
	}

	private setResizeFunction() {
		// Modal vertical resize, if horizontal scrollbar becomes present. This is very specific to the drilldown stylesheet.
		const jModal = $('#' + this.dialogId);
		const tabPane = $('#' + this.dialogId + ' .tab-pane');
		const drilldownContent = $(
			'#' + this.dialogId + ' .drilldown-tab-content',
		);
		let lastHeight;
		this.resizeFunction = () => {
			const minWidthBeforeScroll = parseFloat(
				drilldownContent.css('width'),
			);
			const currentWidth = tabPane.width();

			if (lastHeight !== jModal.height()) {
				tabPane.css('height', '');
			}
			lastHeight = jModal.height();

			// horizontal scroller will be present on modal. May need to shrink vertical height to accommodate it
			if (currentWidth < minWidthBeforeScroll) {
				const scrollDifference =
					jModal.get(0).scrollHeight - jModal.innerHeight();

				if (scrollDifference > 1) {
					// vertical scroller will be present, need to shrink modal
					const height = tabPane.css('height', '').css('height');
					tabPane.css(
						'height',
						parseFloat(height) - scrollDifference,
					);
				}
			} else {
				// go back to stylesheet
				tabPane.css('height', '');
			}
		};

		jModal.fadeIn(0, () => {
			$(window).on('resize', this.resizeFunction);
			this.resizeFunction();
		});
		// end resize
	}

	private close() {
		this.controller.cancel();
		$(window).off('resize', this.resizeFunction);
		this.model.parentTable.clearTableSelection();
	}
}
